import {
    MANIFEST_STATUSES,
    PALLET_STATUSES,
    RECEIVED_STATUSES,
    STAGED_STATUSES,
    STORED_STATUSES,
} from '@/constants/manifestStatuses';

export const enrichManifest = (manifest) => {
    const orders = getManifestOrders(manifest);

    const [received, staged, picked_up, total, cubes] = orders.reduce(
        ([accReceived, accStaged, accPickedUp, accTotal, accCubes], order) => {
            const [itemsReceived, itemsStaged, itemsPickedUp, itemsTotal, itemsCubes] = (
                order.manifestItems || []
            ).reduce(
                ([accItemsReceived, accItemsStaged, accItemsPickedUp, accItemsTotal, accItemsCubes], item) => {
                    const qty = item.quantity || 1;
                    const cubes = item.total_cubes || 0;
                    const isReceived = RECEIVED_STATUSES.includes(item?.pallet?.warehouse_status);
                    const isStaged = STAGED_STATUSES.includes(item?.pallet?.warehouse_status);
                    const isPickedUp = item?.pallet?.warehouse_status === PALLET_STATUSES.PICKED_UP;
                    return [
                        accItemsReceived + (isReceived ? qty : 0),
                        accItemsStaged + (isStaged ? qty : 0),
                        accItemsPickedUp + (isPickedUp ? qty : 0),
                        accItemsTotal + qty,
                        accItemsCubes + cubes,
                    ];
                },
                [0, 0, 0, 0, 0]
            );
            return [
                accReceived + itemsReceived,
                accStaged + itemsStaged,
                accPickedUp + itemsPickedUp,
                accTotal + itemsTotal,
                accCubes + itemsCubes,
            ];
        },
        [0, 0, 0, 0, 0]
    );

    let status;
    switch (manifest?.type) {
        case 'CROSS_DOCK':
        case 'INBOUND': {
            status = MANIFEST_STATUSES.NOT_RECEIVED;
            if (received > 0) {
                status = MANIFEST_STATUSES.IN_PROGRESS;
            }
            if (received > 0 && received === total) {
                status = MANIFEST_STATUSES.RECEIVED;
            }
            break;
        }
        case 'WILL_CALL':
        case 'RETURN_TO_SENDER':
        case 'OUTBOUND': {
            status = MANIFEST_STATUSES.AWAITING_STAGING;
            if (staged > 0) {
                status = MANIFEST_STATUSES.IN_PROGRESS;
            }
            if (staged > 0 && staged === total) {
                status = MANIFEST_STATUSES.STAGED;
            }
            if (['inProgress', 'complete'].includes(manifest.route?.status) || (picked_up > 0 && picked_up === total)) {
                status = MANIFEST_STATUSES.COMPLETE;
            }
            break;
        }
    }

    return {
        ...manifest,
        orders,
        status,
        received,
        staged,
        picked_up,
        total,
        cubes,
    };
};

/**
 * `@onward-delivery/core` derives Item X / Y and carton totals from `order.itemsByOrderId` iteration
 * sequence, but emits label rows in `order.manifestItems` sequence. Web loads full nested `itemsByOrderId`
 * on each order while `manifestItems` follows manifest line order — numbers no longer match print order.
 * For printing, force both lists to the same manifest-scoped sequence for consistent item ordering.
 */
export const alignManifestForItemLabelPrinting = (manifest) => {
    if (!manifest || !Array.isArray(manifest.orders) || manifest.orders.length === 0) {
        return manifest;
    }
    return {
        ...manifest,
        orders: manifest.orders.map((order) => {
            const manifestItems = order.manifestItems || [];
            const fromOrder = order.itemsByOrderId;
            const useManifestLines = manifestItems.length > 0;
            return {
                ...order,
                itemsByOrderId: useManifestLines
                    ? manifestItems
                    : Array.isArray(fromOrder) && fromOrder.length > 0
                    ? fromOrder
                    : [],
            };
        }),
    };
};

/**
 * Order statuses a route-linked manifest's grid leaves out. The Manifests list search
 * (`buildManifestSearchFilter`) skips the same statuses for route item matches, so a manifest listed by an
 * item opens to a grid with that item.
 * @type {string[]}
 */
export const HIDDEN_ROUTE_ORDER_STATUSES = ['cancelled', 'rejected', 'rescheduled', 'deleted'];

/**
 * The orders a manifest's grid shows, each with its `manifestItems`. Route-linked manifests (any type but
 * INBOUND) use the route's orders, minus hidden statuses, return orders on OUTBOUND, and non-pickup legs on
 * CROSS_DOCK. Everything else groups `manifest.items` by order. `ROUTE_ORDER_VISIBILITY`
 * (`Manifests/utils.js`) mirrors these rules for list search by route item; change both together.
 * @param {object} manifest Manifest with `type`, `items`, and optionally `route.orders`.
 * @returns {object[]} Orders with `manifestItems`.
 */
export const getManifestOrders = (manifest) => {
    const shouldExcludeOrder = (order) => manifest?.type === 'OUTBOUND' && order?.order_type === 'return';

    // INBOUND always sources members from manifest.items, even when route-linked (matches manifest_summary_view).
    if (manifest?.route && manifest?.type !== 'INBOUND') {
        return (manifest.route?.orders || [])
            .filter((mapping) => {
                // Only show pickup -> crossdock leg orders for CROSS_DOCK type manifests
                const crossdock = manifest.type !== 'CROSS_DOCK' || mapping.type === 'PICKUP';
                return (
                    crossdock &&
                    !shouldExcludeOrder(mapping.order) &&
                    !HIDDEN_ROUTE_ORDER_STATUSES.includes(mapping.order.order_status)
                );
            })
            .map((mapping) => ({
                ...mapping.order,
                manifestItems: mapping.order.itemsByOrderId,
            }));
    }
    const itemsByOrderId = (manifest?.items || []).reduce((grouped, { item }) => {
        if (shouldExcludeOrder(item?.order)) {
            return grouped;
        }
        return {
            ...grouped,
            [item.order_id]: {
                ...item.order,
                manifestItems: [...(grouped[item.order_id]?.manifestItems || []), item],
            },
        };
    }, {});
    return Object.values(itemsByOrderId);
};
